import ImageUpload from 'components/form/fields/ImageUpload'
import {CROP_PURPOSES, cropPurposeSize} from 'components/form/fields/ImageCropModal'
import {createValue} from 'components/form/utils'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useRef, useState} from 'react'
import classNames from 'classnames'
import {NullAsDefault} from 'views/Shared'
import {IoImage} from 'react-icons/io5'
import {BiTrash} from 'react-icons/bi'
import {PiCloudArrowUp} from 'react-icons/pi'

// Field upload ảnh có khung (ảnh bìa khóa học / ảnh mẫu chứng nhận / ...): empty state icon cloud
// + "Tải ảnh lên / Duyệt tập tin", filled state có overlay pill + tên file + nhãn kích thước cố định.
// Wrap ImageUpload, chọn ảnh xong sẽ mở ImageCropModal (theo cropPurpose) để cắt/căn về đúng kích thước.
// Mặc định các prop = ảnh bìa khóa học (cropPurpose="course_cover") -> nơi khác truyền prop để đổi.

// Tên file từ URL ảnh đã lưu (không áp dụng cho data: URI vừa upload).
const urlBasename = (src) => {
    if (!src || typeof src !== 'string' || src.startsWith('data:')) return null
    try {
        const clean = src.split('?')[0].split('#')[0]
        return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1)) || null
    } catch (e) {
        return null
    }
}

const CourseCoverField = ({
    name,
    value,
    values,
    onChange = NullAsDefault,
    // Trường hợp dùng -> quyết định kích thước cố định + tỉ lệ khung crop.
    cropPurpose = 'course_cover',
    formatsText = 'PNG, JPG, WEBP',
    maxSizeText = '4MB',
    // undefined -> dùng default; string -> đổi text.
    selectedLabel,
    // undefined -> default; false/'' /null -> ẩn hẳn dòng ghi chú.
    note,
    previewStyle = {height: '26vw', minHeight: 150, maxHeight: 260},
}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const previewName = `${name}_preview`
    const previewImage = _.get(values, previewName, value)

    const resolvedSelected = selectedLabel === undefined ? t('cover selected') : selectedLabel
    const resolvedNote = note === undefined ? t('cover display note') : note

    const cfg = CROP_PURPOSES[cropPurpose] || CROP_PURPOSES.course_cover
    const sizeLabel = cropPurposeSize(cropPurpose)
    const recommendedSizeText = `${sizeLabel} (${t(cfg.labelKey)})`

    // Tên file ảnh vừa chọn - bắt qua native "change" của <input type=file> antd đặt trong wrapper.
    // Ảnh cũ chỉ có URL -> lấy basename.
    const wrapRef = useRef(null)
    const [uploadedName, setUploadedName] = useState(null)
    useEffect(() => {
        const el = wrapRef.current
        if (!el) return undefined
        const onNativeChange = (e) => {
            const input = e.target
            if (input && input.type === 'file' && input.files && input.files[0]) {
                setUploadedName(input.files[0].name)
            }
        }
        el.addEventListener('change', onNativeChange, true)
        return () => el.removeEventListener('change', onNativeChange, true)
    }, [])
    const fileName = uploadedName || urlBasename(previewImage)

    const clear = (e) => {
        e.stopPropagation()
        onChange(createValue(name, null))
        onChange(createValue(previewName, null))
        setUploadedName(null)
    }

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {/* course-cover-uploader: ép antd Upload wrapper block/full-width (mặc định inline-block
                khiến khung dùng % bị co về 0) - xem tailwind_custom.css */}
            <div ref={wrapRef} className="course-cover-uploader w-full">
                <ImageUpload
                    name={name}
                    value={value}
                    onChange={onChange}
                    cropPurpose={cropPurpose}
                    renderPreview={NullAsDefault}
                    showUploadList={false}>
                    <div
                        style={{width: '100%', position: 'relative', ...previewStyle}}
                        className={classNames(
                            'rounded-xl overflow-hidden cursor-pointer transition-all duration-300',
                            previewImage
                                ? 'border border-color-100'
                                : 'border-2 border-dashed border-gray-400 hover:border-blue-400 background hover:bg-slate-50'
                        )}>
                        <div className="absolute inset-0 flex flex-center">
                            {previewImage ? (
                                <div className="cover-preview-wrap w-full h-full relative">
                                    <img
                                        src={previewImage}
                                        alt=""
                                        className="cover-preview-img"
                                        style={{width: '100%', height: '100%', objectFit: 'cover', background: '#f1f1f1'}}
                                    />

                                    {/* Pill "đã chọn" - góc trên trái */}
                                    <span className="absolute top-10 left-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black bg-opacity-40 backdrop-blur-sm text-2xs font-medium text-white whitespace-no-wrap">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0"/>
                                        {resolvedSelected}
                                    </span>

                                    {/* Tên file ảnh đã chọn (fallback: basename URL của ảnh cũ) */}
                                    {fileName && (
                                        <span
                                            style={{maxWidth: '60%'}}
                                            className="absolute bottom-10 left-10 px-2 py-0.5 rounded-md bg-black bg-opacity-40 backdrop-blur-sm text-2xs font-medium text-white whitespace-no-wrap truncate">
                                            {fileName}
                                        </span>
                                    )}

                                    {/* Nhãn kích thước cố định - góc dưới phải */}
                                    {sizeLabel && (
                                        <span className="absolute bottom-10 right-10 px-2 py-0.5 rounded-md text-2xs font-semibold text-white whitespace-no-wrap backdrop-blur-sm bg-slate-700 bg-opacity-80">
                                            {sizeLabel}
                                        </span>
                                    )}

                                    {/* Overlay hành động - hover trên desktop, luôn hiện trên mobile */}
                                    <div
                                        className={classNames(
                                            'absolute inset-0 flex items-center justify-center gap-2 transition-opacity duration-300',
                                            isSm ? 'opacity-100' : 'opacity-40 hover:opacity-100'
                                        )}
                                        style={{background: 'rgba(0,0,0,0.35)'}}>
                                        <span
                                            style={{padding: '6px 12px'}}
                                            className="flex items-center gap-1.5 rounded-lg font-semibold text-xs text-white bg-black bg-opacity-40 hover:bg-opacity-60 backdrop-blur-sm transition-colors">
                                            <IoImage size={13}/>
                                            {t('change photo')}
                                        </span>
                                        <span
                                            onClick={clear}
                                            style={{width: 30, height: 30}}
                                            className="flex flex-center rounded-lg text-white bg-red-500 bg-opacity-80 hover:bg-opacity-100 transition-colors flex-shrink-0">
                                            <BiTrash size={14}/>
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-6 w-full flex flex-col flex-center gap-2 text-center text-color-300">
                                    <div
                                        style={{boxShadow: '0 4px 12px rgba(37, 99, 235, 0.12)'}}
                                        className="flex flex-center w-14 h-14 rounded-2xl background text-blue-500">
                                        <PiCloudArrowUp size={28}/>
                                    </div>
                                    <div className="space-x-1 text-sm">
                                        <span className="font-semibold text-gray-700">{`${t('upload cover hint')} `}</span>
                                        <span className="font-semibold text-blue-600 underline">{t('browse files')}</span>
                                    </div>
                                    <span className="text-xs text-color-400 tracking-wider">
                                        {t('cover format hint', {
                                            m: <span className="font-semibold text-gray-600">{formatsText}</span>,
                                            n: <span className="font-semibold text-gray-600">{maxSizeText}</span>,
                                        })}
                                    </span>
                                    <div className="mt-3 pt-3 w-90 mx-auto border-t border-color-50 flex flex-center">
                                        <span className="text-xs text-color-400 tracking-wider">
                                            {t('recommended cover size', {
                                                m: <span className="font-semibold text-gray-600">{recommendedSizeText}</span>,
                                            })}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </ImageUpload>
            </div>

            {resolvedNote ? (
                <p className="text-2xs text-color-400 italic leading-relaxed tracking-wide">
                    {`* ${resolvedNote}`}
                </p>
            ) : null}
        </div>
    )
}

export default CourseCoverField
